/**
 * Reset Password Dialog Component
 * Generates new temporary password and optionally sends via email
 *
 * @module Features/UserManagement/Dialogs
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Stack,
  Box,
  FormControlLabel,
  Checkbox,
  Paper,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useResetPasswordMutation, useGetUserByIdQuery } from '../Service/usersApi';
import { closeResetPasswordDialog } from '../slices/userManagementSlice';
import { useSnackbar } from 'notistack';

interface ResetPasswordDialogProps {
  userId: number;
}

/**
 * Reset Password Dialog Component
 */
const ResetPasswordDialog: React.FC<ResetPasswordDialogProps> = ({ userId }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { data: user, isLoading: isLoadingUser } = useGetUserByIdQuery(userId);
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [sendEmail, setSendEmail] = useState(true);

  const onReset = async () => {
    try {
      const response = await resetPassword({
        id: userId,
        body: { sendEmail },
      }).unwrap();

      setTempPassword(response.temporaryPassword);
      enqueueSnackbar('Hasło zresetowane pomyślnie', { variant: 'success' });
      console.log('✅ Password has been reset');
    } catch (error: any) {
      const message = error?.data?.message || 'Nie udało się zresetować hasła';
      enqueueSnackbar(message, { variant: 'error' });
      console.error('❌ Error:', message);
    }
  };

  const handleClose = () => {
    setTempPassword(null);
    dispatch(closeResetPasswordDialog());
  };

  const copyPassword = () => {
    if (tempPassword) {
      navigator.clipboard.writeText(tempPassword);
      console.log('📋 Password copied to clipboard');
    }
  };

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={handleClose}>
      <DialogTitle sx={{ fontWeight: 600 }}>Resetuj hasło</DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Stack spacing={2}>
          {isLoadingUser ? (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={40} />
            </div>
          ) : tempPassword ? (
            <>
              <Typography variant="body2">
                Hasło zostało pomyślnie zresetowane dla {user?.email}:
              </Typography>

              <Paper sx={{ p: 2, bgcolor: '#fff3cd' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Tymczasowe hasło:
                </Typography>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '1rem',
                    wordBreak: 'break-all',
                    mb: 1,
                  }}
                >
                  {tempPassword}
                </Box>
                <Button fullWidth variant="outlined" size="small" onClick={copyPassword}>
                  Kopiuj hasło
                </Button>
              </Paper>

              <Alert severity="info">
                <Typography variant="body2">
                  • Udostępnij to hasło użytkownikowi bezpiecznym kanałem
                </Typography>
                <Typography variant="body2">
                  • Użytkownik będzie musiał zmienić hasło przy następnym logowaniu
                </Typography>
                {sendEmail && (
                  <Typography variant="body2">
                    • Hasło zostało wysłane na adres {user?.email}
                  </Typography>
                )}
              </Alert>
            </>
          ) : (
            <>
              <Typography variant="body2">Wygeneruj nowe tymczasowe hasło dla:</Typography>

              <Alert severity="info">
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  Email: {user?.email}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 1 }}>
                  Imię i nazwisko: {user?.firstName} {user?.lastName}
                </Typography>
              </Alert>

              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Wyślij hasło mailem na adres {user?.email}
                    </Typography>
                  }
                />
              </Box>

              <Alert severity="warning">
                <Typography variant="body2">
                  • Zostanie wygenerowane nowe bezpieczne losowe hasło
                </Typography>
                <Typography variant="body2">• Zobaczysz hasło tylko raz</Typography>
                <Typography variant="body2">
                  • Użytkownik musi zmienić je przy pierwszym logowaniu
                </Typography>
              </Alert>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Zamknij
        </Button>
        {!tempPassword && (
          <Button
            onClick={onReset}
            variant="contained"
            color="warning"
            disabled={isLoading || isLoadingUser}
            startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
          >
            {isLoading ? 'Resetowanie...' : 'Resetuj hasło'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ResetPasswordDialog;
